import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Upload, Save, Eye, EyeOff, Building2, Image, Globe, QrCode, Download } from "lucide-react";

export default function PlatformSettings({ user }) {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const hostLogoRef = useRef(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/platform/settings`);
      setSettings(res.data);
    } catch (err) {
      toast.error("Kunne ikke hente platformindstillinger");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/platform/settings`, {
        host_name: settings.host_name,
        platform_name: settings.platform_name,
        powered_by_enabled: settings.powered_by_enabled,
        powered_by_text: settings.powered_by_text,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Indstillinger gemt!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fejl ved gem");
    } finally {
      setSaving(false);
    }
  };

  const uploadHostLogo = async (file) => {
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(`${API}/platform/logo`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, host_logo_url: res.data.logo_url }));
      toast.success("Host-logo uploadet!");
    } catch (err) {
      toast.error("Fejl ved logo upload");
    }
  };

  const downloadUniversalQr = async () => {
    try {
      const baseUrl = window.location.origin;
      const res = await axios.get(`${API}/qr-code?base_url=${encodeURIComponent(baseUrl)}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'resovia-qr-universel.png';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("QR-kode downloadet!");
    } catch (err) {
      toast.error("Fejl ved QR download");
    }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Indlæser...</div>;
  if (!settings) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="platform-settings-page">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Building2 className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-bold font-heading uppercase">Platform-indstillinger</h1>
          </div>
          <Button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Gemmer...' : 'Gem indstillinger'}
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-2xl space-y-8">
        
        {/* Platformnavn */}
        <section className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
            <Globe className="h-4 w-4" /> Platform
          </h2>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Platformnavn</Label>
              <Input
                value={settings.platform_name || ''}
                onChange={e => setSettings(prev => ({ ...prev, platform_name: e.target.value }))}
                placeholder="Resovia"
                data-testid="platform-name-input"
              />
            </div>
          </div>
        </section>

        {/* Host/Firma branding */}
        <section className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
            <Building2 className="h-4 w-4" /> Dit firma (host)
          </h2>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Firmanavn</Label>
              <Input
                value={settings.host_name || ''}
                onChange={e => setSettings(prev => ({ ...prev, host_name: e.target.value }))}
                placeholder="OurChoice"
                data-testid="host-name-input"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider">Firma-logo</Label>
              <div className="mt-2 flex items-center gap-4">
                {settings.host_logo_url ? (
                  <div className="h-16 w-16 rounded border border-border bg-zinc-800 flex items-center justify-center p-1">
                    <img 
                      src={`${API.replace('/api', '')}${settings.host_logo_url}`} 
                      alt="Host logo" 
                      className="max-h-full max-w-full object-contain" 
                    />
                  </div>
                ) : (
                  <div className="h-16 w-16 rounded border border-dashed border-border bg-zinc-900 flex items-center justify-center">
                    <Image className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <input 
                    ref={hostLogoRef}
                    type="file" 
                    accept="image/png,image/jpeg,image/webp,image/svg+xml" 
                    className="hidden" 
                    onChange={(e) => e.target.files?.[0] && uploadHostLogo(e.target.files[0])} 
                  />
                  <Button variant="outline" size="sm" onClick={() => hostLogoRef.current?.click()} data-testid="upload-host-logo-btn">
                    <Upload className="h-3 w-3 mr-2" />
                    {settings.host_logo_url ? 'Skift logo' : 'Upload logo'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">PNG, JPG, WEBP eller SVG</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Powered by */}
        <section className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
            {settings.powered_by_enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            "Powered by" sektion
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Vis "Powered by" branding</p>
                <p className="text-xs text-muted-foreground">Vises i bunden af login og event-vælger</p>
              </div>
              <Button 
                variant={settings.powered_by_enabled ? "default" : "outline"}
                size="sm"
                onClick={() => setSettings(prev => ({ ...prev, powered_by_enabled: !prev.powered_by_enabled }))}
                data-testid="toggle-powered-by-btn"
              >
                {settings.powered_by_enabled ? 'Aktiv' : 'Inaktiv'}
              </Button>
            </div>
            
            {settings.powered_by_enabled && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Tekst</Label>
                  <Input
                    value={settings.powered_by_text || ''}
                    onChange={e => setSettings(prev => ({ ...prev, powered_by_text: e.target.value }))}
                    placeholder="powered by"
                    data-testid="powered-by-text-input"
                  />
                </div>
                
                <div className="bg-zinc-900 rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-2">Forhåndsvisning:</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {settings.host_logo_url && (
                      <img 
                        src={`${API.replace('/api', '')}${settings.host_logo_url}`} 
                        alt="" 
                        className="h-4 w-auto opacity-60" 
                      />
                    )}
                    <span>
                      {settings.platform_name || 'Resovia'} &mdash;{' '}
                      {settings.powered_by_text || 'powered by'}{' '}
                      <span className="font-semibold">{settings.host_name || 'OurChoice'}</span>
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        {/* QR-kode */}
        <section className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
            <QrCode className="h-4 w-4" /> Universel QR-kode
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Download en universel QR-kode der leder til login-siden. Kan deles med alle frivillige og arrangører.
          </p>
          <Button onClick={downloadUniversalQr} variant="outline" data-testid="download-universal-qr-btn">
            <Download className="h-4 w-4 mr-2" />
            Download universel QR-kode
          </Button>
        </section>
      </main>
    </div>
  );
}
